import React, { useEffect, useState, useRef } from "react";
import { TopNav } from "@/components/TopNav";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RankBadge } from "@/components/RankBadge";
import { MessagesSquare, Send, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function Community() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const load = () => api.get("/chat/global?limit=80").then(({ data }) => setMsgs(data));

  useEffect(() => {
    load();
    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "chat.global") setMsgs((m) => [...m, ev.msg].slice(-200));
          if (ev.type === "chat.deleted") setMsgs((m) => m.filter((x) => x.id !== ev.id));
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault();
    const m = text.trim();
    if (!m) return;
    try {
      await api.post("/chat/global", { message: m });
      setText("");
    } catch {}
  };

  const del = async (id) => {
    await api.delete(`/admin/chat/${id}`);
  };

  const isAdmin = user?.role === "admin" || user?.role === "staff";

  return (
    <div className="min-h-screen bg-[#05050a] flex flex-col">
      <TopNav />
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-6 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <MessagesSquare className="text-purple-300" />
          <h1 className="font-display font-bold text-2xl sm:text-3xl">Cộng đồng</h1>
          <span className="text-xs text-white/40">· {msgs.length} tin</span>
        </div>

        <div className="flex-1 rounded-xl bg-[#0c0d14] border border-white/8 p-4 overflow-y-auto space-y-3 mb-3 min-h-[400px] max-h-[60vh]" data-testid="chat-list">
          {msgs.length === 0 ? (
            <div className="text-white/40 text-center py-12 text-sm">Hãy là người đầu tiên gửi tin nhắn!</div>
          ) : msgs.map((m) => {
            const mine = m.sender_id === user?.id;
            // Highlight system arena message
            if (m.is_system || m.room_type === "system") {
              return (
                <div key={m.id} className="flex justify-center my-1">
                  <div className="max-w-[90%] px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500/15 to-yellow-700/5 border border-amber-400/40">
                    <div className="text-sm text-amber-200 mb-1.5 font-medium">{m.message}</div>
                    {m.arena_request_id && (
                      <a href={`/arena/${m.arena_request_id}`} className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-display font-semibold bg-amber-400 text-black hover:bg-amber-300">
                        Xem kèo →
                      </a>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
                <Link to={`/players/${m.sender_id}`} className="shrink-0">
                  <img src={m.sender_avatar} alt="" className="w-8 h-8 rounded-full" />
                </Link>
                <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div className="flex items-center gap-1.5 text-xs text-white/60">
                    <span className="font-medium">{m.sender_nickname}</span>
                    <span className="text-[10px]" style={{ color: m.sender_rank ? undefined : "#8d92a3" }}>· {m.sender_rank}</span>
                  </div>
                  {(() => {
                    const isArena = typeof m.message === "string" && m.message.includes("[ĐẤU TRƯỜNG]");
                    return (
                      <div className={`mt-1 px-3 py-2 rounded-2xl text-sm break-words ${
                        isArena
                          ? "bg-amber-400/15 border border-amber-400/40 text-amber-100"
                          : mine ? "bg-cyan-400/15 border border-cyan-400/30 text-cyan-50" : "bg-white/[0.04] border border-white/8"
                      }`} data-testid={isArena ? `arena-msg-${m.id}` : undefined}>
                        {isArena ? (
                          <span>
                            <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded text-[10px] font-display font-black tracking-widest bg-amber-400 text-black">ĐẤU TRƯỜNG</span>
                            {m.message.replace(/\[ĐẤU TRƯỜNG\]\s*/g, "")}
                          </span>
                        ) : m.message}
                      </div>
                    );
                  })()}
                  <div className="text-[10px] text-white/30 mt-0.5">{new Date(m.created_at).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                {isAdmin && (
                  <button onClick={() => del(m.id)} className="p-1 text-white/30 hover:text-red-400" title="Xóa" data-testid={`del-msg-${m.id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Nhập tin nhắn..." data-testid="chat-input"
            className="flex-1 bg-[#0c0d14] border border-white/10 rounded-md px-4 py-3 text-sm outline-none focus:border-cyan-400/60" />
          <button type="submit" data-testid="chat-send" className="px-5 rounded-md bg-gradient-to-r from-cyan-400 to-purple-500 text-black font-medium flex items-center gap-2">
            <Send size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
